import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import OutlinedDiv from "../common/OutlinedDiv";
import { CHART_COLORS } from "../../constants/ChartColors";

interface ChartData {
  data: { name: string; value: number; color: string }[];
  label: string;
}

interface StatisticsCardProps {
  migrationChartData: ChartData;
  filesChartData: ChartData;
  fileFlagsChartData?: ChartData;
  title: string;
  totalFilesSizeInGb: number;
  totalMigratedFilesSizeInGb: number;
  totalFilesSizeInMb: number;
  totalMigratedFilesSizeInMb: number;
  totalFilesMovedToFirmDocsSizeInGb?: number;
  totalFilesMovedToFirmDocsSizeInMb?: number;
  notResolvedFlaggedFilesSizeInGb?: number;
  notResolvedFlaggedFilesSizeInMb?: number;
  showFlaggedSizeInfo: boolean;
  notFoundFilesSizeInGb?: number;
  notFoundFilesSizeInMb?: number;
  interruptedFilesSizeInGb?: number;
  interruptedFilesSizeInMb?: number;
  skippedDuplicateFilesSizeInGb?: number;
  skippedDuplicateFilesSizeInMb?: number;
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({
  migrationChartData,
  filesChartData,
  fileFlagsChartData,
  title,
  totalFilesSizeInGb,
  totalFilesSizeInMb,
  totalMigratedFilesSizeInMb,
  totalFilesMovedToFirmDocsSizeInMb,
  notResolvedFlaggedFilesSizeInMb,
  showFlaggedSizeInfo,
  notFoundFilesSizeInGb,
  notFoundFilesSizeInMb,
  interruptedFilesSizeInGb,
  interruptedFilesSizeInMb,
  skippedDuplicateFilesSizeInGb,
  skippedDuplicateFilesSizeInMb,
}) => {
  const useGb = totalFilesSizeInGb > 1;

  const formatSize = (mb: number) =>
    useGb ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(2)} MB`;

  const gridColsClass = fileFlagsChartData
    ? "grid-cols-1 @3xl:grid-cols-2 @5xl:grid-cols-3"
    : "grid-cols-1 @3xl:grid-cols-2";

  const renderChartSection = (chartData: ChartData) => {
    const total = chartData.data.reduce((sum, d) => sum + d.value, 0);
    const nonZero = chartData.data.filter(d => d.value > 0);
    const migratedEntry = chartData.data.find(d => d.name === "Migrated");
    const migratedPct =
      migratedEntry && total > 0
        ? Math.round((migratedEntry.value / total) * 100)
        : null;

    return (
      <OutlinedDiv label={chartData.label}>
        {/* Stacked bar */}
        <div className="flex h-3 w-full rounded-full overflow-hidden bg-muted mb-3">
          {total > 0 &&
            nonZero.map((entry, i) => (
              <div
                key={i}
                style={{
                  width: `${(entry.value / total) * 100}%`,
                  backgroundColor: entry.color,
                  minWidth: "2px",
                }}
                title={`${entry.name}: ${entry.value.toLocaleString()}`}
              />
            ))}
        </div>

        {/* Legend — 2 columns, zeros hidden */}
        {nonZero.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            {nonZero.map((entry, i) => (
              <div key={i} className="flex items-center justify-between gap-2 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-xs text-muted-foreground truncate">{entry.name}</span>
                </div>
                <span className="text-xs font-medium tabular-nums shrink-0">
                  {entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No data</p>
        )}

        {/* Footer */}
        <p className="text-xs text-muted-foreground mt-2">
          {migratedPct !== null
            ? `${migratedPct}% migrated · ${total.toLocaleString()} total`
            : `${total.toLocaleString()} total`}
        </p>
      </OutlinedDiv>
    );
  };

  const hasPerStatusSizeData = (skippedDuplicateFilesSizeInMb ?? 0) > 0 || (notFoundFilesSizeInMb ?? 0) > 0;
  const migrationCompletionSizeMb = totalMigratedFilesSizeInMb + (skippedDuplicateFilesSizeInMb ?? 0) + (notFoundFilesSizeInMb ?? 0);
  const migrationCompletionPercentage =
    totalFilesSizeInMb > 0
      ? `${((migrationCompletionSizeMb / totalFilesSizeInMb) * 100).toFixed(2)}%`
      : totalFilesSizeInMb === 0
      ? "100.00%"
      : "0.00%";

  // "Moved to Firm Docs" files are migrated (status 3), so their size is already inside totalMigratedFilesSizeInMb —
  // it's a subset of migrated, not a peer. The bar is a simple migrated-vs-remaining split (Remaining = total -
  // migrated, which includes held/flagged files); "Moved to Firm Docs" is surfaced in the legend nested under the
  // migrated total. Capped at migrated so it can never exceed it.
  const fileSizeMovedMb = showFlaggedSizeInfo
    ? Math.min(totalFilesMovedToFirmDocsSizeInMb ?? 0, totalMigratedFilesSizeInMb)
    : 0;

  const fileSizeSegments = [
    { name: "Migrated", mb: totalMigratedFilesSizeInMb, color: CHART_COLORS.migrated },
    { name: "Remaining", mb: Math.max(0, totalFilesSizeInMb - totalMigratedFilesSizeInMb), color: CHART_COLORS.notStarted },
  ].filter(s => s.mb > 0);

  // Legend rows: the migrated total, with "Moved to Firm Docs" nested beneath it as a subset, then remaining.
  const fileSizeLegendRows: { name: string; mb: number; color?: string; indent?: boolean }[] = [];
  for (const seg of fileSizeSegments) {
    fileSizeLegendRows.push({ name: seg.name, mb: seg.mb, color: seg.color });
    if (seg.name === "Migrated" && fileSizeMovedMb > 0)
      fileSizeLegendRows.push({ name: "Moved to Firm Docs", mb: fileSizeMovedMb, indent: true });
  }

  const fileSizeTotalLabel = useGb
    ? `${totalFilesSizeInGb.toFixed(2)} GB`
    : `${totalFilesSizeInMb.toFixed(2)} MB`;

  const fileSizeMigratedPct =
    totalFilesSizeInMb > 0
      ? Math.round((totalMigratedFilesSizeInMb / totalFilesSizeInMb) * 100)
      : null;

  return (
    <Card className="w-full">
      <CardContent>
        <h3 className="text-xl font-semibold pb-5">{title}</h3>
        <div className="grid grid-cols-1 gap-1">

          {/* Files Size — stacked bar */}
          <OutlinedDiv label="Files Size">
            <div className="flex h-3 w-full rounded-full overflow-hidden bg-muted mb-3">
              {totalFilesSizeInMb > 0 &&
                fileSizeSegments.map((seg, i) => (
                  <div
                    key={i}
                    style={{
                      width: `${(seg.mb / totalFilesSizeInMb) * 100}%`,
                      backgroundColor: seg.color,
                      minWidth: "2px",
                    }}
                    title={`${seg.name}: ${formatSize(seg.mb)}`}
                  />
                ))}
            </div>

            {fileSizeLegendRows.length > 0 ? (
              <div className="flex flex-col gap-1">
                {fileSizeLegendRows.map((row, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between gap-2 min-w-0${row.indent ? " pl-4" : ""}`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      {row.color ? (
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: row.color }}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground/70 shrink-0">↳</span>
                      )}
                      <span
                        className={`text-xs truncate ${row.indent ? "text-muted-foreground/80" : "text-muted-foreground"}`}
                      >
                        {row.name}
                      </span>
                    </div>
                    <span
                      className={`text-xs tabular-nums shrink-0 ${row.indent ? "text-muted-foreground/80" : "font-medium"}`}
                    >
                      {formatSize(row.mb)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No data</p>
            )}

            <p className="text-xs text-muted-foreground mt-2">
              {fileSizeMigratedPct !== null
                ? `${fileSizeMigratedPct}% migrated · ${fileSizeTotalLabel} total`
                : `${fileSizeTotalLabel} total`}
            </p>

            {hasPerStatusSizeData && (
              <p className="text-xs text-muted-foreground mt-1">
                Migration Completion: {formatSize(migrationCompletionSizeMb)} ({migrationCompletionPercentage})
                {" · "}Skipped Duplicate: {formatSize(skippedDuplicateFilesSizeInMb ?? 0)}
                {" · "}Not Found: {formatSize(notFoundFilesSizeInMb ?? 0)}
              </p>
            )}
          </OutlinedDiv>

          {/* Charts */}
          <div className="@container">
            <div className={`grid ${gridColsClass} gap-1`}>
              <div>{renderChartSection(migrationChartData)}</div>
              <div>{renderChartSection(filesChartData)}</div>
              {fileFlagsChartData && (
                <div>{renderChartSection(fileFlagsChartData)}</div>
              )}
            </div>
          </div>

        </div>
      </CardContent>
    </Card>
  );
};

export default StatisticsCard;
